/**
 * Report Export Utilities
 * Shared helpers for branded Excel/PDF exports across all reports.
 */
import * as XLSX from 'xlsx';
import { county_logo } from '@/api';

/**
 * Default company name – can be overridden per-call.
 */
const DEFAULT_COMPANY = '';

/**
 * Insert branded header rows at the top of an XLSX worksheet.
 *
 * Adds 5 rows:
 *   Row 1: Company Name
 *   Row 2: Report Title
 *   Row 3: Period
 *   Row 4: Generated At
 *   Row 5: (blank separator)
 *
 * Then shifts all existing data 5 rows down.
 *
 * @param {object} ws        – XLSX worksheet
 * @param {object} opts
 * @param {string} opts.companyName
 * @param {string} opts.reportTitle
 * @param {string} opts.period      – e.g. "01 Jun 2026 – 22 Jun 2026"
 * @param {string} [opts.department]
 * @param {number} colCount          – number of data columns (for merge range)
 * @returns {object} the modified worksheet
 */
export function addBrandedHeader(ws, { companyName, reportTitle, period, department } = {}, colCount = 10) {
  const company = companyName || DEFAULT_COMPANY;
  const now = new Date();
  const generatedAt = now.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Header rows to prepend
  const headerRows = [
    [company],
    [reportTitle || 'Report'],
    [`Report Period: ${period || 'N/A'}`],
    [`Generated: ${generatedAt}${department ? '  |  Department: ' + department : ''}`],
    [] // blank separator
  ];

  // Get the current range
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

  // Shift all existing cells down by 5 rows
  for (let r = range.e.r; r >= range.s.r; r--) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const oldAddr = XLSX.utils.encode_cell({ r, c });
      const newAddr = XLSX.utils.encode_cell({ r: r + 5, c });
      if (ws[oldAddr]) {
        ws[newAddr] = ws[oldAddr];
        delete ws[oldAddr];
      }
    }
  }

  // Insert header rows
  headerRows.forEach((row, ri) => {
    row.forEach((val, ci) => {
      const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
      ws[addr] = { v: val, t: 's' };
    });
  });

  // Update range
  range.e.r += 5;
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: range.e.r, c: Math.max(range.e.c, colCount - 1) }
  });

  // Merge header cells across columns
  if (!ws['!merges']) ws['!merges'] = [];
  const lastCol = Math.max(range.e.c, colCount - 1);
  for (let i = 0; i < 4; i++) {
    ws['!merges'].push({
      s: { r: i, c: 0 },
      e: { r: i, c: lastCol }
    });
  }

  return ws;
}

/**
 * Build branded header rows as an array-of-arrays (for prepending before data).
 * Use when building worksheets from scratch with aoa_to_sheet.
 *
 * @param {object} opts
 * @returns {Array<Array>} 5 rows to prepend
 */
export function buildBrandedHeaderRows({ companyName, reportTitle, period, department } = {}) {
  const company = companyName || DEFAULT_COMPANY;
  const now = new Date();
  const generatedAt = now.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return [
    [company],
    [reportTitle || 'Report'],
    [`Report Period: ${period || 'N/A'}`],
    [`Generated: ${generatedAt}${department ? '  |  Department: ' + department : ''}`],
    [] // blank separator
  ];
}

/**
 * Open a new window with branded print-friendly HTML for PDF generation.
 *
 * @param {object} opts
 * @param {string} opts.reportTitle
 * @param {string} opts.period
 * @param {string} opts.companyName
 * @param {string} [opts.department]
 * @param {Array<string>} opts.columns   – table header labels
 * @param {Array<Array<string>>} opts.rows – table body data
 * @param {object} [opts.summary]         – key-value pairs for summary section
 */
export function generateBrandedPDF({
  reportTitle,
  period,
  companyName,
  department,
  columns,
  rows,
  summary
} = {}) {
  const company = companyName || DEFAULT_COMPANY;
  const now = new Date();
  const generatedAt = now.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Build summary HTML
  let summaryHtml = '';
  if (summary && Object.keys(summary).length > 0) {
    summaryHtml = `
      <div class="summary-section">
        <h3>Summary</h3>
        <div class="summary-grid">
          ${Object.entries(summary).map(([k, v]) => `
            <div class="summary-item">
              <span class="summary-label">${k}</span>
              <span class="summary-value">${v}</span>
            </div>
          `).join('')}
        </div>
      </div>`;
  }

  // Build table HTML
  let tableHtml = '';
  if (columns && columns.length > 0) {
    const headerRow = columns.map(c => `<th>${c}</th>`).join('');
    const bodyRows = (rows || []).map(row =>
      '<tr>' + row.map(cell => `<td>${cell ?? ''}</td>`).join('') + '</tr>'
    ).join('');

    tableHtml = `
      <table>
        <thead><tr>${headerRow}</tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>`;
  }

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${reportTitle || 'Report'} – ${company}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', -apple-system, sans-serif; color: #1e293b; padding: 20px; }

    .header {
      display: flex; align-items: center; gap: 20px;
      border-bottom: 3px solid #6366f1; padding-bottom: 16px; margin-bottom: 24px;
    }
    .header img { max-width: 60px; max-height: 60px; object-fit: contain; }
    .header-info { flex: 1; }
    .header-company { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; color: #6366f1; }
    .header-title { font-size: 20px; font-weight: 700; margin: 2px 0 6px; }
    .header-meta { font-size: 12px; color: #64748b; }
    .header-meta span { margin-right: 20px; }

    .confidential {
      text-align: right; font-size: 10px; text-transform: uppercase;
      letter-spacing: 1px; color: #ef4444; font-weight: 600;
    }

    .summary-section { margin-bottom: 24px; }
    .summary-section h3 { font-size: 14px; margin-bottom: 10px; color: #475569; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    .summary-grid { display: flex; flex-wrap: wrap; gap: 12px; }
    .summary-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 14px; min-width: 140px; }
    .summary-label { display: block; font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
    .summary-value { font-size: 16px; font-weight: 700; color: #1e293b; }

    table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 8px; }
    th { background: #1e293b; color: #fff; padding: 8px 10px; text-align: left; font-weight: 600; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; }
    td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
    tr:nth-child(even) td { background: #f8fafc; }
    tr:hover td { background: #eef2ff; }

    .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 10px; color: #94a3b8; }

    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="text-align:right;margin-bottom:12px;">
    <button onclick="window.print()" style="padding:8px 20px;background:#6366f1;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;">
      🖨️ Print / Save as PDF
    </button>
  </div>

  <div class="header">
    <img src="${county_logo}" alt="Company Logo" onerror="this.style.display='none'">
    <div class="header-info">
      ${company ? `<div class="header-company">${company}</div>` : ''}
      <div class="header-title">${reportTitle || 'Report'}</div>
      <div class="header-meta">
        <span>📅 Period: ${period || 'N/A'}</span>
        <span>🕐 Generated: ${generatedAt}</span>
        ${department ? `<span>🏢 Department: ${department}</span>` : ''}
      </div>
    </div>
  </div>

  ${summaryHtml}
  ${tableHtml}

  <div class="footer">
    Generated &bull; ${generatedAt}
  </div>
</body>
</html>`;

  const printWindow = window.open('', '_blank', 'width=1100,height=800');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}

/**
 * Format GPS coordinates and return an object with display text and Google Maps URL.
 *
 * @param {number|string} lat
 * @param {number|string} lng
 * @returns {{ text: string, url: string|null }}
 */
export function formatCoordinatesWithLink(lat, lng) {
  const a = parseFloat(lat);
  const b = parseFloat(lng);
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return { text: '-', url: null };
  }
  const text = `${a.toFixed(6)}, ${b.toFixed(6)}`;
  const url = `https://www.google.com/maps?q=${a},${b}`;
  return { text, url };
}

/**
 * Get the company name from session storage or return the default.
 */
export function getCompanyName() {
  try {
    const info = JSON.parse(sessionStorage.getItem('companyInfo') || '{}');
    return info.companyName || info.name || DEFAULT_COMPANY;
  } catch {
    return DEFAULT_COMPANY;
  }
}
